import { useState } from "react";
import { format } from "date-fns";
import { ArrowLeft, Plus } from "lucide-react";

import { HealthlinkCredentialsModal } from "components/HealthlinkCredentialsModal/HealthlinkCredentialsModal.jsx";
import { ProcessMessagesButton } from "components/ProcessMessagesButton/ProcessMessagesButton";
import { Button } from "components/ui";
import { Dialog } from "components/ui/Dialog/Dialog";
import { useHealthlinkCredentials } from "features/healthlink/hooks/useHealthlinkCredentials";
import { useHealthlinkPractice } from "features/healthlink/hooks/useHealthlinkPractice";
import { useLabOrder } from "features/labOrders/hooks/useLabOrder";
import { useLabOrderFacilities } from "features/labOrders/hooks/useLabOrderFacilities";
import { areMockLabFacilitiesEnabled } from "features/labOrders/mockFacilities";
import { getLabOrderPreflightViolations } from "features/labOrders/utils/labOrderPreflight";
import { SimplePatientPageLayout } from "features/patients/components/SimplePatientPageLayout/SimplePatientPageLayout";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { formatDate } from "utils/dateFormatter";

import { LabOrderDetails } from "./components/LabOrderDetails/LabOrderDetails";
import { LabOrderIbHandoff } from "./components/LabOrderIbHandoff/LabOrderIbHandoff";
import { LabOrderNotices } from "./components/LabOrderNotices/LabOrderNotices";
import { LabOrdersTable } from "./components/LabOrdersTable/LabOrdersTable";
import { LabOrderStatusPill } from "./components/LabOrderStatusPill/LabOrderStatusPill";
import { LabOrderSteps } from "./components/LabOrderSteps/LabOrderSteps";
import { NewLabOrderForm } from "./components/NewLabOrderForm/NewLabOrderForm";
import { useIbLabOrderReturn } from "./hooks/useIbLabOrderReturn";
import { labOrdersViews, useLabOrdersView } from "./hooks/useLabOrdersView";
import s from "./PatientLabOrdersPage.module.css";

// eLab laboratory ordering for one patient (KIN-1390). The clinical content of an
// order — tests, clinical details, specimen, priority — is entered in Healthlink's
// own form, not here: this page names the destination laboratory, hands the GP over,
// and tracks what the laboratory answered.
//
// The patient and the laboratory directory are fetched here rather than in the form,
// because the section's notices depend on both: a GP should learn that ordering is
// unavailable from the list, not after opening the form.
const PatientLabOrdersPage = () => {
  const patientId = useCurrentPatientId();
  const [view, setView] = useLabOrdersView();
  // Only set when the redirect out to Healthlink did not happen — then the GP gets the
  // copy-link fallback, exactly as a referral does.
  const [failedRedirect, setFailedRedirect] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const { data: healthlinkPractice } = useHealthlinkPractice();
  const { data: healthlinkCredentials } = useHealthlinkCredentials();
  const { data: practiceSettings } = usePracticeSettings();
  const { data: patient } = usePatientData(patientId);
  const {
    data: facilities,
    isLoading: areFacilitiesLoading,
    isError: hasFacilitiesError,
    refetch: refetchFacilities,
  } = useLabOrderFacilities();

  // Confirm-on-return from the Healthlink Integrated Browser; keeps the list hidden
  // until the returned order has been settled.
  const { isProcessingReturn } = useIbLabOrderReturn();

  const hasHealthlinkCredentials =
    !!healthlinkPractice &&
    Array.isArray(healthlinkCredentials) &&
    healthlinkCredentials.length > 0;

  const preflightViolations = getLabOrderPreflightViolations(patient);
  // Laboratory ordering is a per-practice feature flag (KIN-1532). Compared against
  // false explicitly, so a backend that does not report it yet is not read as "off".
  const isOrderingDisabled = practiceSettings?.labOrdering === false;
  const hasNoFacilities =
    !areFacilitiesLoading && !hasFacilitiesError && facilities?.length === 0;
  const canPlaceOrder =
    hasHealthlinkCredentials &&
    !isOrderingDisabled &&
    !hasNoFacilities &&
    preflightViolations.length === 0;

  const returnToList = () => {
    setFailedRedirect(null);
    setSelectedOrder(null);
    setView(labOrdersViews.list);
  };

  // Shares its query key with the detail view's own call, so React Query serves both
  // from one request — the header must not show a status the section below contradicts.
  const { data: openedOrder } = useLabOrder(selectedOrder?.id);
  const detailOrder = openedOrder ?? selectedOrder;

  return (
    <SimplePatientPageLayout>
      <div className={s.secondColumnSubWrapper}>
        <div className={s.column}>
          <LabOrderNotices
            patientId={patientId}
            preflightViolations={preflightViolations}
            isOrderingDisabled={isOrderingDisabled}
            hasNoFacilities={hasNoFacilities}
            areMockFacilitiesEnabled={areMockLabFacilitiesEnabled()}
          />

          {view === labOrdersViews.list && (
            <div className={s.mainWrapper}>
              <div className={s.titleWrapper}>
                <h1 className={s.title}>eLab Orders</h1>
                <div className={s.headerActions}>
                  <ProcessMessagesButton
                    title="Process lab order responses"
                    successMessage="Lab order responses processed successfully!"
                  />
                  {hasHealthlinkCredentials ? (
                    <Button
                      variant="primary"
                      endIcon={Plus}
                      onClick={() => setView(labOrdersViews.newOrder)}
                      disabled={!canPlaceOrder}
                    >
                      Add lab order
                    </Button>
                  ) : (
                    <HealthlinkCredentialsModal />
                  )}
                </div>
              </div>
              <div className={s.content}>
                <LabOrdersTable
                  patientId={patientId}
                  isBusy={isProcessingReturn}
                  onSelectOrder={(order) => {
                    setSelectedOrder(order);
                    setView(labOrdersViews.details);
                  }}
                />
              </div>
            </div>
          )}

          {view === labOrdersViews.newOrder && (
            <div className={`${s.mainWrapper} ${s.mainWrapperFlush}`}>
              <div className={`${s.titleWrapper} ${s.titleWrapperPadded}`}>
                <div className={s.backButtonWrapper}>
                  <Button
                    variant="outline"
                    size="small"
                    className={s.backButton}
                    aria-label="Back to lab orders"
                    onClick={returnToList}
                  >
                    <ArrowLeft />
                  </Button>
                  <h1 className={s.title}>
                    Lab order{" "}
                    <span className={s.titleDate}>
                      {format(new Date(), "dd-MM-yyyy")}
                    </span>
                  </h1>
                </div>
              </div>

              <LabOrderSteps />

              <div className={s.content}>
                <NewLabOrderForm
                  patientId={patientId}
                  patient={patient}
                  facilities={facilities}
                  areFacilitiesLoading={areFacilitiesLoading}
                  hasFacilitiesError={hasFacilitiesError}
                  onRetryFacilities={refetchFacilities}
                  isBlockedByPatientDetails={preflightViolations.length > 0}
                  onRedirectFailed={setFailedRedirect}
                  onBack={returnToList}
                />
              </div>
            </div>
          )}

          {view === labOrdersViews.details && !!selectedOrder && (
            <div className={`${s.mainWrapper} ${s.mainWrapperFlush}`}>
              <div className={`${s.titleWrapper} ${s.titleWrapperPadded}`}>
                <div className={s.backButtonWrapper}>
                  <Button
                    variant="outline"
                    size="small"
                    className={s.backButton}
                    aria-label="Back to lab orders"
                    onClick={returnToList}
                  >
                    <ArrowLeft />
                  </Button>
                  <h1 className={s.title}>
                    Lab order{" "}
                    <span className={s.titleDate}>
                      {formatDate(selectedOrder.created)}
                    </span>
                  </h1>
                </div>
                <LabOrderStatusPill order={detailOrder} />
              </div>
              <div className={s.previewPanel}>
                <LabOrderDetails
                  order={selectedOrder}
                  onOrderCompleted={returnToList}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Shown only when the redirect out to Healthlink did not take — the GP finishes the
          order from the copy-link instead. The same fallback the referral form ends on. */}
      <Dialog
        open={!!failedRedirect}
        title="Complete lab order in Healthlink"
        onClose={() => setFailedRedirect(null)}
        width={640}
      >
        <LabOrderIbHandoff
          submission={failedRedirect}
          onFinished={returnToList}
          isFlush
        />
      </Dialog>
    </SimplePatientPageLayout>
  );
};

export { PatientLabOrdersPage };
